// Colunares municipais, detalhe por UF e zonas (§14.4).
import * as z from 'zod/mini';
import {
  type CandidateNumber,
  CandidateNumberSchema,
  CandidatePairSchema,
  type FeedHeader,
  feedHeaderShape,
  type IbgeCode,
  IbgeCodeSchema,
  IntSchema,
  type Office,
  OfficeSchema,
  type PlaceCode,
  PlaceCodeSchema,
} from './base';
import { type Tally, tallyShape } from './tally';

/** {feed}/s/{seq}/municipal.json (imutável). */
export interface MunicipalColumns extends FeedHeader<'results.municipal'> {
  /** 'president' no 2º turno. */
  office: Office;
  /** seq do snapshot com que foi publicado. */
  snapshot_seq: number;
  /** = Geography.registry.id (I-13). */
  registry_id: string;
  candidates: [CandidateNumber, CandidateNumber];
  fraction_scale: 10000;
  /** Tamanho = registry.n_municipalities. */
  brazil: ColumnBlock;
  /** Tamanho = registry.n_abroad. */
  abroad: ColumnBlock;
}
export interface ColumnBlock {
  /** Aptos totalizados / aptos × 10000; −1 = sem informação. */
  electorate_fraction: number[];
  /** Seções totalizadas / seções × 10000 (opcional, recomendado). */
  sections_fraction?: number[];
  /** Votos de candidates[0]. */
  votes_a: number[];
  /** Votos de candidates[1]. */
  votes_b: number[];
  /** t_min (inteiro) da última mudança; −1 = nunca. */
  changed_t_min?: number[];
}

/** {feed}/s/{seq}/state/{uf}.json (UF minúscula; "zz" = Exterior) — imutável, sob demanda. */
export interface StateDetail extends FeedHeader<'results.state_detail'> {
  office: Office;
  place: PlaceCode;
  registry_id: string;
  /** IBGE nas UFs; código TSE da cidade no ZZ. */
  keys: string[];
  columns: {
    sections: number[];
    sections_counted: number[];
    electorate: number[];
    electorate_counted: number[];
    turnout: number[];
    blank_votes: number[];
    null_votes: number[];
    votes: Record<CandidateNumber, number[]>;
    /** Hora do arquivo do município em t_min; −1 se não houver. */
    tse_ht_t_min: number[];
  };
}

/** {feed}/zones/{ibge}.json — mutável, P2. */
export interface ZoneResults extends FeedHeader<'results.zones'> {
  office: Office;
  ibge: IbgeCode;
  rows: Array<{ zone: number; place_name: string | null } & Tally>;
}

const ints = z.array(IntSchema);

export const ColumnBlockSchema = z.object({
  electorate_fraction: ints,
  sections_fraction: z.optional(ints),
  votes_a: ints,
  votes_b: ints,
  changed_t_min: z.optional(ints),
});

export const MunicipalColumnsSchema = z.object({
  ...feedHeaderShape('results.municipal'),
  office: OfficeSchema,
  snapshot_seq: IntSchema,
  registry_id: z.string(),
  candidates: CandidatePairSchema,
  fraction_scale: z.literal(10000),
  brazil: ColumnBlockSchema,
  abroad: ColumnBlockSchema,
});

export const StateDetailSchema = z.object({
  ...feedHeaderShape('results.state_detail'),
  office: OfficeSchema,
  place: PlaceCodeSchema,
  registry_id: z.string(),
  keys: z.array(z.string()),
  columns: z.object({
    sections: ints,
    sections_counted: ints,
    electorate: ints,
    electorate_counted: ints,
    turnout: ints,
    blank_votes: ints,
    null_votes: ints,
    votes: z.record(CandidateNumberSchema, ints),
    tse_ht_t_min: z.array(z.number()),
  }),
});

export const ZoneResultsSchema = z.object({
  ...feedHeaderShape('results.zones'),
  office: OfficeSchema,
  ibge: IbgeCodeSchema,
  rows: z.array(z.object({ zone: IntSchema, place_name: z.nullable(z.string()), ...tallyShape })),
});
